import { useMemo } from 'react';
import keyBy from 'lodash/keyBy';
import type { Column } from '@atlassian/jira-business-board-workflow-issues';
import {
	GROUP_BY_ASSIGNEE,
	GROUP_BY_CATEGORY,
	GROUP_BY_PRIORITY,
	GROUP_BY_STATUS,
} from '@atlassian/jira-business-constants';
import {
	statusCategoryForId,
	StatusCategoryIds,
} from '@atlassian/jira-common-constants/src/status-categories';
import { ASSIGNEE_TYPE, PRIORITY_TYPE, CATEGORY_TYPE } from '@atlassian/jira-platform-field-config';
import { NULL_GROUP_KEY } from '../../common/constants';
import type { BoardColumn, BoardIssueOld } from '../../common/types';
import { useBoardFilters } from '../../services/board-filters-old';
import { useBoardStore } from '../../services/board-store';
import type {
	Issue,
	IssuesWithFields,
	IssueTypesMap,
	IssueFieldsMap,
	PeopleMap,
	StatusMap,
} from '../../services/board-store/types';
import {
	isGroupByFieldAllowedByKey,
	isGroupByFieldAllowedByType,
} from '../../services/board-store/utils';
import { useCollapsedColumns } from '../board-collapse-columns-old';
import { useGetColumnsOrder } from '../columns-order-old';
import { useGroupBy } from '../group-by';

export const UNPRIORITISED_COLUMN_NAME = 'Unprioritised';
export const UNASSIGNED_COLUMN_NAME = 'Unassigned';
export const UNCATEGORISED_COLUMN_NAME = 'Uncategorised';

const getDoneStatusIds = (columns: Array<Column>) =>
	columns.filter((column) => column.statusCategory === 'done').map((column) => column.statusId);

const issueToBoardIssue = ({
	issue,
	issueTypes,
	issuesWithFields,
	people,
	statuses,
	doneStatusIds,
	columnId,
}: {
	issue: Issue;
	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
	issueTypes: IssueTypesMap | void;
	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
	issuesWithFields: IssuesWithFields | void;
	people: PeopleMap;
	statuses?: StatusMap;
	doneStatusIds: number[];
	columnId: string | number;
}): BoardIssueOld => {
	const issueType =
		issue.issueTypeId != null && issueTypes != null ? issueTypes[issue.issueTypeId] : undefined;
	const typeName = issueType?.name;

	const statusDetails =
		statuses && statuses[issue.statusId]
			? {
					...statuses[issue.statusId],
				}
			: {};

	return {
		...issue,
		issueDetails: issuesWithFields?.[issue.id],
		columnId,
		priority:
			issue.priorityIconUrl != null && issue.priorityName != null
				? {
						name: issue.priorityName,
						iconUrl: issue.priorityIconUrl,
					}
				: null,
		numTotalChildren: issue.subtaskMetadata?.total,
		numCompleteChildren: issue.subtaskMetadata?.done,
		assignee:
			issue.assigneeKey != null && people
				? { ...people[issue.assigneeKey], id: issue.assigneeKey }
				: null,
		isDone: doneStatusIds.includes(issue.statusId),
		typeName,
		typeUrl: issueType?.avatarUrl ?? '',
		subtasks: issue.subtasks,
		parentId: null,
		...statusDetails,
	};
};

type ColumnBuilder = (args: {
	columns: Column[];
	issues: Issue[];
	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
	issuesWithFields: IssuesWithFields | void;
	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
	issueFields: IssueFieldsMap | void;
	people: PeopleMap;
	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
	issueTypes: IssueTypesMap | void;
	filter: (issues: BoardIssueOld[]) => BoardIssueOld[];
	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
	collapsedColumnKeys: string[] | void;
}) => BoardColumn[];

const getColumnsByStatus: ColumnBuilder = ({
	columns,
	issues,
	issuesWithFields,
	people,
	issueTypes,
	filter,
	collapsedColumnKeys,
}) => {
	const doneStatusIds = getDoneStatusIds(columns);

	return columns.map((column) => {
		const columnIssues = issues
			.filter((issue) => issue.statusId === column.statusId)
			.map((issue) =>
				issueToBoardIssue({
					issue,
					issueTypes,
					issuesWithFields,
					people,
					doneStatusIds,
					columnId: column.statusId,
				}),
			);
		return {
			...column,
			id: column.statusId,
			groupKey: String(column.statusId),
			isDone: column.statusCategory === 'done',
			isInitial: false,
			issues: filter(columnIssues),
			clearedOutCardsUrl: null,
			totalCardsCount: columnIssues.length,
			isCollapsed:
				collapsedColumnKeys != null && collapsedColumnKeys.includes(String(column.statusId)),
			groupBy: GROUP_BY_STATUS,
			color: null,
		};
	});
};

const createGroupColumns = ({
	groupConfig,
	columns,
	issuesWithFields,
	people,
	issueTypes,
	filter,
	collapsedColumnKeys,
}: {
	groupConfig: {
		// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
		key: 'priorityId' | 'assigneeKey' | ((issue: Issue) => string | void);
		createColumn: (key: string, issue: Issue) => BoardColumn;
	};
	columns: Column[];
	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
	issuesWithFields: IssuesWithFields | void;
	people: PeopleMap;
	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
	issueTypes: IssueTypesMap | void;
	filter: (issues: BoardIssueOld[]) => BoardIssueOld[];
	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
	collapsedColumnKeys: string[] | void;
}) => {
	const doneStatusIds = getDoneStatusIds(columns);
	const groupColumns = new Map<string, BoardColumn>();
	const statuses = keyBy(
		columns.map((column) => ({
			statusId: column.statusId,
			statusName: column.name,
			statusCategory: statusCategoryForId(
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				StatusCategoryIds[column.statusCategory as keyof typeof StatusCategoryIds],
			),
		})),
		(status) => status.statusId,
	);
	return {
		getColumns: () =>
			[...groupColumns.values()].map((column) => ({
				...column,
				issues: filter(column.issues),
				totalCardsCount: column.issues.length,
			})),
		addIssue: (issue: Issue) => {
			const maybeKey =
				typeof groupConfig.key === 'function' ? groupConfig.key(issue) : issue[groupConfig.key];
			const groupKey = maybeKey != null && maybeKey !== '' ? String(maybeKey) : NULL_GROUP_KEY;
			let column = groupColumns.get(groupKey);

			if (!column) {
				column = groupConfig.createColumn(groupKey, issue);
				groupColumns.set(groupKey, column);
				column.isCollapsed = collapsedColumnKeys != null && collapsedColumnKeys.includes(groupKey);
			}

			column.issues.push(
				issueToBoardIssue({
					issue,
					issueTypes,
					issuesWithFields,
					people,
					statuses,
					doneStatusIds,
					columnId: groupKey,
				}),
			);
		},
	};
};

const getColumnsByPriority: ColumnBuilder = ({
	columns,
	issues,
	issuesWithFields,
	people,
	issueTypes,
	filter,
	collapsedColumnKeys,
}) => {
	const groupColumns = createGroupColumns({
		groupConfig: {
			key: 'priorityId',
			createColumn(key, issue) {
				return {
					clearedOutCardsUrl: null,
					isDone: false,
					isInitial: false,
					statusCategory: '',
					statusId: 0,
					id: key,
					name: issue.priorityName ?? UNPRIORITISED_COLUMN_NAME,
					canCreateIssues: true,
					hasFilteredIssues: false,
					groupBy: GROUP_BY_PRIORITY,
					groupKey: key,
					imageUrl: issue.priorityIconUrl ?? null,
					issues: [],
					totalCardsCount: 0,
					color: null,
				};
			},
		},
		columns,
		issuesWithFields,
		people,
		issueTypes,
		filter,
		collapsedColumnKeys,
	});
	issues.forEach((issue) => groupColumns.addIssue(issue));
	return groupColumns.getColumns();
};

const getColumnsByAssignee: ColumnBuilder = ({
	columns,
	issues,
	issuesWithFields,
	people,
	issueTypes,
	filter,
	collapsedColumnKeys,
}) => {
	const groupColumns = createGroupColumns({
		groupConfig: {
			key: 'assigneeKey',
			createColumn(key: string) {
				const assignee = people[key];
				return {
					clearedOutCardsUrl: null,
					isDone: false,
					isInitial: false,
					statusCategory: '',
					statusId: 0,
					id: key,
					name: assignee?.displayName ?? UNASSIGNED_COLUMN_NAME,
					canCreateIssues: true,
					hasFilteredIssues: false,
					groupBy: GROUP_BY_ASSIGNEE,
					groupKey: key,
					imageUrl: assignee?.avatarUrl ?? null,
					issues: [],
					totalCardsCount: 0,
					color: null,
				};
			},
		},
		columns,
		issuesWithFields,
		people,
		issueTypes,
		filter,
		collapsedColumnKeys,
	});
	issues.forEach((issue) => groupColumns.addIssue(issue));
	return groupColumns.getColumns();
};

const getColumnsByCategory: ColumnBuilder = ({
	columns,
	issues,
	issuesWithFields,
	issueFields,
	people,
	issueTypes,
	filter,
	collapsedColumnKeys,
}) => {
	const categoryFieldFromAvailableFields =
		issueFields != null
			? Object.values(issueFields).find((field) => field.type === CATEGORY_TYPE)
			: undefined;

	const categoryFieldId = categoryFieldFromAvailableFields?.id;
	const categoryFieldName = categoryFieldFromAvailableFields?.name;

	const getIssueCategory = (issue: Issue) =>
		issuesWithFields?.[issue.id]?.fieldsByAlias?.category?.value;
	const getIssueCategoryColor = (issue: Issue) =>
		issuesWithFields?.[issue.id]?.fieldsByAlias?.category?.color;
	const getIssueCategoryId = (issue: Issue) =>
		issuesWithFields?.[issue.id]?.fieldsByAlias?.category?.id;

	const groupColumns = createGroupColumns({
		groupConfig: {
			key: getIssueCategoryId,
			createColumn(key, issue) {
				const issueCategory = getIssueCategory(issue);

				const getGroupKey = (columnIssue: Issue) =>
					getIssueCategoryId(columnIssue) ?? NULL_GROUP_KEY;

				return {
					canCreateIssues: true,
					clearedOutCardsUrl: null,
					hasFilteredIssues: false,
					id: key,
					isDone: false,
					isInitial: false,
					issues: [],
					name: issueCategory ?? UNCATEGORISED_COLUMN_NAME,
					color: getIssueCategoryColor(issue) ?? null,
					groupFieldKey: categoryFieldId,
					groupKey: getGroupKey(issue),
					groupName: categoryFieldName,
					groupBy: GROUP_BY_CATEGORY,
					statusCategory: '',
					statusId: 0,
					totalCardsCount: 0,
				};
			},
		},
		columns,
		issuesWithFields,
		people,
		issueTypes,
		filter,
		collapsedColumnKeys,
	});
	issues.forEach((issue) => groupColumns.addIssue(issue));
	return groupColumns.getColumns();
};

/** @deprecated Please use `useGroupBuilder` - remove when cleaning up jwm.board-awesome-enhancements */
export const columnBuilders = {
	[GROUP_BY_STATUS]: getColumnsByStatus,
	[GROUP_BY_PRIORITY]: getColumnsByPriority,
	[GROUP_BY_ASSIGNEE]: getColumnsByAssignee,
	[GROUP_BY_CATEGORY]: getColumnsByCategory,
};

/** @deprecated Please use `useGroups` - remove when jwm.board-awesome-enhancements */
export const useColumns = () => {
	const { filter } = useBoardFilters();
	const [groupBy] = useGroupBy();
	const [collapsedColumnKeys] = useCollapsedColumns();
	const getColumnsOrder = useGetColumnsOrder();
	const [{ issueFields, columns, issueTypes, issues, issuesWithFields, people }] = useBoardStore();

	return useMemo(() => {
		const possibleGroupType =
			(groupBy === GROUP_BY_PRIORITY &&
				isGroupByFieldAllowedByKey(PRIORITY_TYPE, issueFields, issueTypes) &&
				GROUP_BY_PRIORITY) ||
			(groupBy === GROUP_BY_ASSIGNEE &&
				isGroupByFieldAllowedByKey(ASSIGNEE_TYPE, issueFields, issueTypes) &&
				GROUP_BY_ASSIGNEE) ||
			(groupBy === GROUP_BY_CATEGORY &&
				isGroupByFieldAllowedByType(CATEGORY_TYPE, issueFields, issueTypes) &&
				GROUP_BY_CATEGORY) ||
			GROUP_BY_STATUS;
		if (possibleGroupType === GROUP_BY_STATUS) {
			return getColumnsByStatus({
				columns,
				issues,
				issuesWithFields,
				issueFields,
				people,
				issueTypes,
				filter,
				collapsedColumnKeys,
			});
		}

		return getColumnsOrder(
			columnBuilders[possibleGroupType]({
				columns,
				issues,
				issuesWithFields,
				issueFields,
				people,
				issueTypes,
				filter,
				collapsedColumnKeys,
			}),
			issueFields,
		);
	}, [
		collapsedColumnKeys,
		columns,
		filter,
		getColumnsOrder,
		groupBy,
		issueFields,
		issueTypes,
		issues,
		issuesWithFields,
		people,
	]);
};
